<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">

      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center">
          <!-- Date From -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field outlined dense hide-details :value="formatDateFrom" label="Od datuma"
                  prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" class="filter-select" />
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable :max="today" @input="reportFromMenu = false" />
            </v-menu>
          </v-col>

          <!-- Date To — ograničeno na isti mjesec kao Od -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field outlined dense hide-details :value="formatDateTo" label="Do datuma"
                  prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" class="filter-select" />
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable :min="minDateTo" :max="maxDateTo" @input="reportToMenu = false" />
            </v-menu>
          </v-col>

          <!-- Location Select -->
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-autocomplete
              outlined
              dense
              hide-details
              label="Prodajno mjesto"
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              return-object
              class="filter-select">
            </v-autocomplete>
          </v-col>

          <!-- Uključi neplaćene račune -->
          <v-col cols="auto" class="d-flex align-center">
            <div class="switch-wrapper">
              <v-switch v-model="includeUnpaid" label="Uključi neplaćene račune" hide-details class="mt-0 pt-0 custom-switch" color="#3989C9"></v-switch>
            </div>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="locationNotSelected" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Report Content -->
      <div class="report-content-wrapper">
        <div class="d-flex justify-center">
          <div id="table_info" class="report-tables">
            <!-- Company Info Table (Hidden by default, shown for PDF) -->
            <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
              <tbody>
                <tr>
                  <td>{{ company ? $options.filters.capitalize(company.name) : "" }}</td>
                </tr>
                <tr>
                  <td>{{ company ? company.address + " " + company.city : "" }}</td>
                </tr>
                <tr>
                  <td>OIB: {{ company ? company.oib : "" }}</td>
                </tr>
                <tr>
                  <td>Prodajno mjesto: {{ location ? location.name : "" }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Report Title -->
            <div class="report-title-wrapper">
              <table justify="center" style="width: 100%" cellspacing="0" id="title">
                <thead>
                  <tr class="header">
                    <th colspan="6">
                      <p class="report-main-title">Zbirni promet</p>
                    </th>
                  </tr>
                </thead>
              </table>
            </div>

            <!-- Data Table -->
            <div class="glass-table-wrapper">
              <table justify="center" style="width: 100%" cellspacing="0" id="groupageTable">
                <colgroup>
                  <col width="20%" />
                  <col width="16%" />
                  <col width="16%" />
                  <col width="16%" />
                  <col width="16%" />
                  <col width="16%" />
                </colgroup>
                <thead>
                  <tr :class="['header', 'naslov']">
                    <th colspan="6">
                      <span class="table-section-title">Promet po danima</span>
                    </th>
                  </tr>
                  <tr :class="['header', 'naslov']">
                    <th style="text-align: left">Datum</th>
                    <th style="text-align: right">Gotovina</th>
                    <th style="text-align: right">Kartice</th>
                    <th style="text-align: right">Transakcijski</th>
                    <th style="text-align: right">Ostalo</th>
                    <th style="text-align: right">Ukupno</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.dayKey" style="text-align: right">
                    <td style="text-align: left">{{ formatDayKey(row.dayKey) }}</td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(row.cash, 100, '').replace('kn', '')
                        : row.cash / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(row.card, 100, '').replace('kn', '')
                        : row.card / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(row.trans, 100, '').replace('kn', '')
                        : row.trans / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(row.ostalo, 100, '').replace('kn', '')
                        : row.ostalo / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(row.total, 100, '').replace('kn', '')
                        : row.total / 100 }}
                    </td>
                  </tr>
                  <tr v-if="rows.length > 0" class="row-total" style="text-align: right">
                    <td style="text-align: left"><strong>Ukupno</strong></td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(totals.cash, 100, '').replace('kn', '')
                        : totals.cash / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(totals.card, 100, '').replace('kn', '')
                        : totals.card / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(totals.trans, 100, '').replace('kn', '')
                        : totals.trans / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(totals.ostalo, 100, '').replace('kn', '')
                        : totals.ostalo / 100 }}
                    </td>
                    <td>
                      {{ viewType === 'pdf'
                        ? $options.filters.money(totals.total, 100, '').replace('kn', '')
                        : totals.total / 100 }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum } from 'firebase/firestore'
import { clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdf, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'

export default {
  name: 'GroupageCollection',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  data () {
    const today = new Date().toISOString().substr(0, 10)
    return {
      dateFrom: today,
      dateTo: today,
      reportFromMenu: false,
      reportToMenu: false,
      company: undefined,
      location: null,
      locations: [],
      rows: [],
      totals: { cash: 0, card: 0, trans: 0, ostalo: 0, total: 0 },
      includeUnpaid: false,
      tableWidth: '1200px',
      a4PortraitWidth: '575px',
      viewType: 'pdf',
      targetModifier: '',
      margin: '0px'
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    today () {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(this.dateFrom))
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(this.dateTo))
    },
    minDateTo () {
      return this.dateFrom ? this.dateFrom.substr(0, 7) + '-01' : undefined
    },
    maxDateTo () {
      if (!this.dateFrom) return this.today
      const [y, m] = this.dateFrom.split('-').map(Number)
      const d = new Date(y, m, 0)
      const lastOfMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return lastOfMonth < this.today ? lastOfMonth : this.today
    },
    locationNotSelected () {
      return !this.location || !this.location.id
    }
  },
  watch: {
    dateFrom (val) {
      if (!this.dateTo.startsWith(val.substr(0, 7))) {
        const [y, m] = val.split('-').map(Number)
        const d = new Date(y, m, 0)
        this.dateTo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
    }
  },
  async created () {
    this.margin = state.getMargin()
    await this.getPointsOfSale()
  },
  mounted () {
    const sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Company':
        this.targetModifier = 'company'
        break
      case 'PointOfSale':
      case 'CashRegister':
        this.targetModifier = 'pointOfSale'
        break
    }

    EventBus.$on('set-mini', () => {
      this.margin = state.getMargin()
    })

    this.company = clone(state.getCurrentCompany())
    this.location = clone(state.getPointOfSale())
  },
  methods: {
    formatDayKey (dayKey) {
      return new Intl.DateTimeFormat('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(dayKey * 1000))
    },
    async getData () {
      this.showLoader()
      this.rows = []
      this.totals = { cash: 0, card: 0, trans: 0, ostalo: 0, total: 0 }

      try {
        const companyId = state.getCurrentCompany().id
        const db = getFirestore()
        const col = collection(db, `receipts/${companyId}/receipts`)

        const from = new Date(this.dateFrom)
        const to = new Date(this.dateTo)
        const days = []
        const d = new Date(from)
        const end = new Date(to)
        while (d <= end) {
          days.push(new Date(d))
          d.setUTCDate(d.getUTCDate() + 1)
        }

        const METHODS = [
          { key: 'NOVČANICE', field: 'cash' },
          { key: 'KARTICA', field: 'card' },
          { key: 'TRANSAKCIJSKI RAČUN', field: 'trans' },
          { key: 'OSTALO', field: 'ostalo' }
        ]

        const dayRows = await Promise.all(days.map(async day => {
          const dayStart = Math.floor(day.getTime() / 1000)
          const dayEnd = dayStart + 86399
          const base = [
            where('issue_date_time', '>=', dayStart),
            where('issue_date_time', '<=', dayEnd)
          ]
          if (this.location?.id && this.location.id !== 'all') {
            base.push(where('location_details.id', '==', this.location.id))
          }

          if (!this.includeUnpaid) {
            base.push(where('payment.current_status', 'in', ['PAID', 'REVERSED']))
          }
          const row = { dayKey: dayStart, cash: 0, card: 0, trans: 0, ostalo: 0 }
          await Promise.all(METHODS.map(async m => {
            const snap = await getAggregateFromServer(
              query(col, ...base, where('payment_method', '==', m.key)),
              { t: sum('total') }
            )
            row[m.field] = snap.data().t || 0
          }))
          row.total = row.cash + row.card + row.trans + row.ostalo
          return row
        }))

        this.rows = dayRows.sort((a, b) => a.dayKey - b.dayKey)
        this.totals = this.rows.reduce((acc, r) => ({
          cash: acc.cash + r.cash,
          card: acc.card + r.card,
          trans: acc.trans + r.trans,
          ostalo: acc.ostalo + r.ostalo,
          total: acc.total + r.total
        }), { cash: 0, card: 0, trans: 0, ostalo: 0, total: 0 })
      } catch (err) {
        console.error('GroupageCollection:', err)
      } finally {
        this.hideLoader()
      }
    },
    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4PortraitWidth
      tableInfo.style.maxWidth = this.a4PortraitWidth
      tableInfo.style.fontSize = '80%'
      this.generatePdf()
    },
    async generatePdf () {
      const that = this
      var doc = await initPdf()

      doc = await setTable(doc, '#firma', 10)
      var finalY = doc.lastAutoTable.finalY || 10
      doc = await setTable(doc, '#title', finalY)
      finalY = doc.lastAutoTable.finalY || 10
      doc = await setTable(doc, '#groupageTable', finalY)

      doc = await setPageNumber(doc)
      doc.save('Zbirni promet.pdf')

      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = '100%'
      tableInfo.style.maxWidth = that.tableWidth
      tableInfo.style.fontSize = '100%'
      this.viewType = 'pdf'
      that.hideLoader()
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        saveExcel(['firma', 'title', 'groupageTable'], 'Zbirni promet.xlsx')
        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    },
    async getPointsOfSale () {
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      docRef.onSnapshot(doc => {
        const locations = Object.values(doc.data().locations)
        locations.forEach(location => {
          this.locations.push(location)
        })
        this.locations.sort((a, b) => a.name.localeCompare(b.name))
      })
    }
  }
}
</script>

<style scoped>
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.switch-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 4px;
}

.custom-switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.custom-switch >>> .v-label {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

.search-btn:disabled {
  opacity: 0.45 !important;
}

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

.report-content-wrapper {
  margin-top: 40px;
}

.report-tables {
  width: 100%;
  max-width: 1200px;
  font-size: 16px;
  font-family: 'Roboto-Regular';
  margin: 0 auto;
}

@media (max-width: 1200px) {
  .report-tables {
    max-width: 100%;
    overflow-x: auto;
  }
}

.report-title-wrapper {
  margin-bottom: 8px;
}

.report-main-title {
  font-weight: 700;
  font-size: 24px;
  color: #2d3748;
  -webkit-text-fill-color: #2d3748;
  letter-spacing: 0.3px;
  display: inline-block;
  margin: 0;
  padding: 10px 0;
}

.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 16px;
  overflow: hidden;
}

.glass-table-wrapper table { border-collapse: collapse; width: 100%; border: none; }
.glass-table-wrapper table th, .glass-table-wrapper table td { padding: 14px 20px; border: none; border-bottom: 1px solid #EDF2F7; font-variant-numeric: tabular-nums; }
.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) { background: #F7FAFC; }

.table-section-title {
  font-weight: bold;
  font-size: 18px;
  color: #1a1a1a;
}

.naslov { background: #F8FAFC !important; font-weight: 700 !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 1px !important; color: #2D3748 !important; }
.naslov th, .naslov td { color: #2D3748 !important; font-weight: 700 !important; }
.row-total { background: rgba(0, 188, 212, 0.03) !important; font-weight: 700 !important; border-top: 2px solid rgba(0, 188, 212, 0.2) !important; color: #00838F !important; }
.row-total td, .row-total th { color: #00838F !important; font-weight: 700 !important; border-bottom: 2px solid rgba(0, 188, 212, 0.2) !important; }
</style>
